import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Cart, useShowDonationItem, useShowStoreItem } from '@givebacks/api/store';
import { useListValidator } from 'core/Hooks';
import { atom, createStore, useAtom, useAtomValue } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import {
  useShowCart,
  useShowCartItem,
  useShowCartItemStoreItem,
  useShowPendingCartFees,
} from '../../api/new/carts/get';
import { useCrowdfundingProvider } from '../crowdfunding/Crowdfunding.provider';
import { cartIdAtom, storeCauseIdAtom, useStoreProvider } from '../store/store.provider';

/**
 * This provider can be used to checkout any cart
 */
type DonationsCheckoutContext = {
  validator: ReturnType<typeof useListValidator>;
  store: ReturnType<typeof createStore>;
};
const DonationsCheckoutContext = createContext<undefined | DonationsCheckoutContext>(undefined);

/**
 * Current Donation Checkout cause id
 */
const causeIdAtom = atom(
  (get) => get(storeCauseIdAtom),
  (_get, set, id: string) => set(storeCauseIdAtom, id)
);

/**
 * Current Donation Checkout cart id
 */
const donationCartIdAtom = atomWithDefault((get) => get(cartIdAtom));
donationCartIdAtom.debugLabel = 'donationCartIdAtom';
/**
 * Current Partial Donation Cart
 */
const donationCartAtom = atom<Partial<Cart> | undefined>(undefined);
donationCartAtom.debugLabel = 'donationsCheckout_CartAtom';
/**
 * Pending Donation Cart Changes
 */
const pendingCartChangesAtom = atom<Partial<Cart> | undefined>(undefined);
pendingCartChangesAtom.debugLabel = 'donationsCheckout_PendingCartChanges';

const DonationCheckoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [store] = useState(() => createStore());
  const validator = useListValidator();
  const storeCartId = useAtomValue(cartIdAtom);
  const storeCauseId = useAtomValue(storeCauseIdAtom);
  useEffect(() => {
    if (storeCartId) {
      const donationCartId = store.get(donationCartIdAtom);
      if (donationCartId === storeCartId) return;

      store.set(donationCartIdAtom, storeCartId);
    }
  }, [storeCartId]);

  // Ensure we forward the set of the causeId
  useEffect(() => {
    if (storeCauseId) {
      const localCauseId = store.get(causeIdAtom);
      // Don't set if it's the same
      if (storeCauseId == localCauseId) return;

      store.set(causeIdAtom, storeCauseId);
    }
  }, [storeCauseId]);

  return (
    <DonationsCheckoutContext.Provider value={{ store, validator: validator }}>
      {children}
    </DonationsCheckoutContext.Provider>
  );
};

const useStoreItemByType = ({ cartId, causeId }: { cartId?: string; causeId?: string }) => {
  const { campaignId } = useCrowdfundingProvider();
  const { data: cartStoreItem } = useShowCartItemStoreItem(cartId);
  const cartItemId = cartStoreItem?.uuid;

  const isStoreItemQueryEnabled = Boolean(cartItemId || campaignId);

  const storeItemResponse = useShowStoreItem({
    causeId,
    // Prefer to use the store itemID from the current cart if it exists.
    uuid: cartItemId ?? campaignId,
  });

  const donationItemResponse = useShowDonationItem({ storeId: causeId });
  // IF we have a campaignId, we are in a campaign context and should fetch show that item, otherwise show the donation item
  const response = useMemo(
    () => (isStoreItemQueryEnabled ? storeItemResponse : donationItemResponse),
    [donationItemResponse, isStoreItemQueryEnabled, storeItemResponse]
  );
  return response;
};

const useDonationsCheckout = () => {
  const { cartCardTypeAtom } = useStoreProvider();
  const cartCardType = useAtomValue(cartCardTypeAtom);

  const causeId = useAtomValue(causeIdAtom);
  const context = useContext(DonationsCheckoutContext);
  if (context === undefined) {
    throw new Error('useDonationsCheckout must be used within a useDonationsCheckoutProvider!');
  }
  const { validator, store } = context as DonationsCheckoutContext;

  const [cartId, setCartIdAtom] = useAtom(donationCartIdAtom, { store });
  const { data: cartItem } = useShowCartItem(cartId);

  return {
    validator,
    causeIdAtom,
    causeId,
    cartId,
    setCartIdAtom,
    useShowCart: () => useShowCart({ cartId }),
    useShowPendingCartFees: () => useShowPendingCartFees(cartId, cartCardType),
    cartItem, // This is the cart item, don't need 'useShowItem' for this
    useStoreItem: () => useStoreItemByType({ cartId, causeId }),
    cartCardType,
  };
};

export { DonationCheckoutProvider, causeIdAtom as donationCauseIdAtom, useDonationsCheckout };
